import { Button, Chip, Table, Tooltip } from '@heroui/react'

import { EyeIcon, PencilIcon, RecipeIcon } from '@/components/ui/icons'

import type { StockItem, StockLevel } from '../types'
import {
  canHaveRecipe,
  hasDetail,
  isCounted,
  itemStatusLabels,
  itemTypeLabels,
  quantity,
  stockStatus,
  stockStatusLabels,
  stockValue,
  unitCost,
} from './inventory-format'
import type { StockStatus } from './inventory-format'

interface StockItemsTableProps {
  items: StockItem[]
  /**
   * One location's levels, by item id. `null` shows each item's totals across
   * every location instead.
   */
  levels: ReadonlyMap<string, StockLevel> | null
  onOpen: (item: StockItem) => void
  onEdit: (item: StockItem) => void
  onRecipe: (item: StockItem) => void
  showDefaultCost?: boolean
}

/** The figures a row reads, wherever they came from. */
type Quantities = Pick<StockItem, 'onHand' | 'available'>

/** A quiet dash: no figure for this cell, and no zero pretending to be one. */
function Dash() {
  return <span className="text-muted">—</span>
}

const stockStatusColors = {
  in: 'success',
  low: 'warning',
  recipe: 'accent',
  out: 'danger',
  untracked: 'default',
} as const satisfies Record<StockStatus, string>

/** In stock reads as the normal case; low and out are the ones to notice. */
function StatusChip({ status }: { status: StockStatus }) {
  return (
    <Chip size="sm" variant="soft" color={stockStatusColors[status]}>
      {stockStatusLabels[status]}
    </Chip>
  )
}

export function StockItemsTable({
  items,
  levels,
  onOpen,
  onEdit,
  onRecipe,
  showDefaultCost = false,
}: StockItemsTableProps) {
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label="Stock items">
          <Table.Header>
            <Table.Column id="item" isRowHeader>
              Item
            </Table.Column>
            <Table.Column id="type">Type</Table.Column>
            <Table.Column id="category">Category</Table.Column>
            <Table.Column id="unit">Unit</Table.Column>
            <Table.Column id="onHand">On hand</Table.Column>
            <Table.Column id="reorderLevel">Reorder level</Table.Column>
            {showDefaultCost && <Table.Column id="defaultCost">Default cost</Table.Column>}
            <Table.Column id="value">Value</Table.Column>
            <Table.Column id="status">Status</Table.Column>
            <Table.Column id="actions">
              <span className="sr-only">Actions</span>
            </Table.Column>
          </Table.Header>

          <Table.Body>
            {items.map((item) => {
              const counted = isCounted(item)
              const figures: Quantities | undefined = levels ? levels.get(item.id) : item

              return (
                <Table.Row key={item.id} id={item.id}>
                  <Table.Cell>
                    <span className="flex flex-col">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-muted text-xs">
                        {item.sku}
                        {item.status !== 'ACTIVE' && ` · ${itemStatusLabels[item.status]}`}
                      </span>
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className="text-muted">{itemTypeLabels[item.stockItemType]}</span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className={item.category ? undefined : 'text-muted'}>
                      {item.category?.name ?? '—'}
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className="text-muted">{item.unitOfMeasure}</span>
                  </Table.Cell>

                  <Table.Cell>
                    {counted && figures ? (
                      <span
                        className={`font-medium tabular-nums ${figures.onHand < 0 ? 'text-danger' : ''}`}
                      >
                        {quantity(figures.onHand)}
                      </span>
                    ) : (
                      <Dash />
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    {counted ? (
                      <span className="text-muted tabular-nums">{quantity(item.reorderPoint)}</span>
                    ) : (
                      <Dash />
                    )}
                  </Table.Cell>

                  {showDefaultCost && (
                    <Table.Cell>
                      <span className="text-muted tabular-nums">
                        {unitCost(item.defaultUnitCost)}
                      </span>
                    </Table.Cell>
                  )}

                  <Table.Cell>
                    {counted && figures ? (
                      <span className={`tabular-nums ${figures.onHand < 0 ? 'text-danger' : ''}`}>
                        {/* The service's own rule: the average cost once a receipt
                            sets one, the default cost until then. */}
                        {stockValue(figures.onHand * (item.currentAvgCost || item.defaultUnitCost))}
                      </span>
                    ) : (
                      <Dash />
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    {!counted ? (
                      <StatusChip status="untracked" />
                    ) : figures ? (
                      <StatusChip status={stockStatus(item, figures.available, Boolean(levels))} />
                    ) : (
                      <Dash />
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    <span className="flex items-center justify-end gap-1.5">
                      {canHaveRecipe(item) && (
                        <Tooltip delay={300}>
                          <Button
                            variant="ghost"
                            size="sm"
                            isIconOnly
                            aria-label={`Recipe for ${item.name}`}
                            onPress={() => onRecipe(item)}
                            className="text-muted hover:text-foreground size-8 rounded-full"
                          >
                            <RecipeIcon width={16} height={16} />
                          </Button>
                          <Tooltip.Content>Recipe</Tooltip.Content>
                        </Tooltip>
                      )}

                      {hasDetail(item) && (
                        <Tooltip delay={300}>
                          <Button
                            variant="ghost"
                            size="sm"
                            isIconOnly
                            aria-label={`Details for ${item.name}`}
                            onPress={() => onOpen(item)}
                            className="text-muted hover:text-foreground size-8 rounded-full"
                          >
                            <EyeIcon width={16} height={16} />
                          </Button>
                          <Tooltip.Content>Details</Tooltip.Content>
                        </Tooltip>
                      )}

                      <Tooltip delay={300}>
                        <Button
                          variant="ghost"
                          size="sm"
                          isIconOnly
                          aria-label={`Edit ${item.name}`}
                          onPress={() => onEdit(item)}
                          className="text-muted hover:text-foreground size-8 rounded-full"
                        >
                          <PencilIcon width={16} height={16} />
                        </Button>
                        <Tooltip.Content>Edit</Tooltip.Content>
                      </Tooltip>
                    </span>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}
